import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { compose, withState, lifecycle, withProps, withStateHandlers } from 'recompose';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import { Link, withRouter } from 'react-router-dom';
import QRCode from 'qrcode.react';
import moment from 'moment';

import { success } from '../../../app/lib/Actions';
import * as SessionActions from '../../../app/entities/Session/actions';
import * as NotificationActions from '../../../app/components/Notifications/actions';
import Notifications from '../../../app/components/Notifications/Notifications';
import { withFetch } from '../../../app/entities/Entity/containers';
import { Endpoints } from '../../../app/lib/LeanLinkService';
import Button from '../../../app/components/Button';
import * as styles from './styles.css';
import LoadingScreen from '../../../app/LoadingScreen/LoadingScreen';
import Routes from '../../Router/Routes';
import * as SessionSelectors from '../../../app/entities/Session/selectors';
import Header from '../../Router/Header';
import QRIcon from './components/QRIcon';
import { sha256 } from "js-sha256";
import { I18nextProvider, useTranslation } from 'react-i18next';
import { useLanguage } from '../../../app/Login/useLanguage';

let initialTicker = 0;
let tickerInterval = 0;

function Login({
  isMobile,
  autoStartToken,
  qrStartToken,
  qrStartSecret,
  mobileUrl,
  toggleQRCode,
  showQRCode,
  secondsDifferenceCET,
  qrAuthCodeCET
}) {
  const { t } = useTranslation();

  const baseURL = __API_BASE_URL__;
  const {
    languages,
    selectedLanguage,
    i18nInstance,
    isLoading,
    handleLanguageChange,
  } = useLanguage(baseURL);

  if (!autoStartToken) {
    return (
      <div>
        <Header />
        <div className="container-fluid">
          <LoadingScreen isLoading position="fixed" />
        </div>
      </div>
    );
  }

  return (
    <I18nextProvider i18n={i18nInstance}>
      <div>
        <Header />
        <div className={styles.languageSelector}>
          <select value={selectedLanguage} onChange={handleLanguageChange}>
            {languages.map((language) => (
              <option key={language.code} value={language.code}>
                {language.name}
              </option>
            ))}
          </select>
        </div>
        <div className="container-fluid">
          <Notifications />
          <div className="row">
            <div
              className={classNames(
                'col-md-4',
                { 'col-md-offset-3': showQRCode, 'col-md-offset-4': !showQRCode },
              )}
            >
              <div className={styles.bankIdLogo}>
                <img src="/images/bankid.png" alt="BankID" className="img-responsive" />
              </div>
              {isMobile && (
                <Button
                  stretch
                  type="submit"
                  variant="success"
                  size="lg"
                  href={mobileUrl}
                  noStyle
                >
                  {t("Logga in")}
                </Button>
              )}
              {!isMobile && !qrStartToken && (
                <div className={styles.loadingQRCodeBox}>
                  <span className={styles.loader}></span>
                  <span className={styles.loaderText}>{t("Laddar QR-kod")}</span>
                </div>
              )
              }
              {qrStartToken && qrAuthCodeCET && showQRCode && (
                <div className="text-center">
                  <p>{t("Skanna in QR-koden med din BankID-app")}</p>
                  <QRCode value={`bankid.${qrStartToken}.${secondsDifferenceCET}.${qrAuthCodeCET}`} size={256} />
                </div>
              )}
              <div className={styles.missingBox}>
                {isMobile && (
                  <div>
                    <a role="button" tabIndex="0" onClick={toggleQRCode}>
                      {t("Har du BankID på en annan enhet?")}
                    </a>
                  </div>
                )}
                <Link to={Routes.missingBankId()}>{t("Saknar du BankID? Klicka här.")}</Link>
              </div>
            </div>
            {showQRCode && (
              <div className="col-md-2">
                <h3 className={classNames('visible-md visible-lg', styles.hiddenHeader)}>&nbsp;</h3>
                <h5 className={styles.header} >{t("Så här gör du")}</h5>
                <ol className={styles.howTo}>
                  <li>{t("Starta BankID-appen")}</li>
                  <li>{t("Tryck på QR-ikonen")} <QRIcon zoom="0.5" /></li>
                  <li>{t("Skanna QR-koden med BankID-appen")}</li>
                  <li>{t("Skriv in din säkerhetskod och tryck")} &quot;{t("Legitimera")}&quot;</li>
                </ol>
              </div>
            )}
          </div>
        </div>
      </div>
    </I18nextProvider>
  );
}

Login.defaultProps = {
  isMobile: false,
  autoStartToken: '',
  mobileUrl: '',
};

Login.propTypes = {
  isMobile: PropTypes.bool,
  autoStartToken: PropTypes.string,
  mobileUrl: PropTypes.string,
  toggleQRCode: PropTypes.func.isRequired,
  showQRCode: PropTypes.bool.isRequired,
};

const getMobileDeviceID = () => {
  const userAgent = navigator.userAgent || navigator.vendor || window.opera;
  if (/windows phone/i.test(userAgent)) {
    return 'android';
  }

  if (/android/i.test(userAgent)) {
    return 'android';
  }

  if (/iPad|iPhone|iPod/.test(userAgent) && !window.MSStream) {
    return 'ios';
  }

  return '';
};

const withAuthenticated = connect(
  state => ({ authenticated: SessionSelectors.authenticated(state) }),
);

export default compose(
  withAuthenticated,
  connect(null,
    dispatch => bindActionCreators({
      addError: NotificationActions.addError,
      twoFactorAuth: success(SessionActions.TWO_FACTOR_AUTH),
    }, dispatch),
  ),
  withState('isCollectLoading', 'setCollectLoading', false),
  withRouter,
  withFetch(),
  lifecycle({
    componentWillMount() {
      if (this.props.authenticated) {
        this.props.history.push(Routes.home());
      } else {
        const fetch = this.props.fetch;
        const slug = window.location.hostname.split('.')[0];

        const pollCollect = (
          staffingCompanyGroupId,
          id,
          newFetchAutoStartToken,
          oldAutostartToken,
        ) => {
          let running = false;
          const intervalId = setInterval((_id) => {
            if (running) {
              return;
            }
            running = true;

            const restartLogin = () => {
              running = false;
              clearInterval(intervalId);
              this.props.setCollectLoading(false);
              newFetchAutoStartToken(staffingCompanyGroupId);
            };
            fetch({
              url: Endpoints.public.bankId.collect({ id: _id }),
              normalization: false,
            }).then((data) => {
              if (data.status !== 'pending') {
                clearInterval(intervalId);
                localStorage.removeItem('pollingData');
                this.props.setCollectLoading(false);
                if (data.token) {
                  this.props.twoFactorAuth(data);
                  this.props.history.push(Routes.home());
                }
                if (data.status === 'failed') {
                  restartLogin();
                }
              } else if (oldAutostartToken) {
                this.setState({ autoStartToken: oldAutostartToken });
              }
              running = false;
            }).catch((restartLogin));
          }, 1000, id);
        };

        const fetchAutoStartToken = (staffingCompanyGroupId) => {
          fetch({
            url: Endpoints.public.bankId.auth({ staffingCompanyGroupId }),
            normalization: false,
          }).then(({ id, autoStartToken, qrStartToken, qrStartSecret, time }) => {
            this.setState({ autoStartToken });
            this.setState({ qrStartToken });
            this.setState({ qrStartSecret });

            if (!initialTicker) {
              let now = new Date();
              var currentTimeUTC = new Date(now.getTime() + now.getTimezoneOffset() * 60000);
              var currentTimeCET = new Date(currentTimeUTC.getTime());
              currentTimeCET.setHours(currentTimeCET.getHours() + 2);

              let initTime = new Date(currentTimeCET);
              initTime.setHours(parseInt(time.split(':')[0]) + 2)
              initTime.setMinutes(time.split(':')[1])
              initTime.setSeconds(time.split(':')[2])

              let secondsDifferenceCET = 0;

              if (!tickerInterval) {
                let _self = this;
                tickerInterval = setInterval(function () {
                  secondsDifferenceCET++;
                  sha256.hmac(qrStartSecret, secondsDifferenceCET.toString());
                  var hashCET = sha256.hmac.create(qrStartSecret);
                  hashCET.update(secondsDifferenceCET.toString());
                  let finalHashCET = hashCET.hex()
                  _self.setState({ secondsDifferenceCET });
                  _self.setState({ qrAuthCodeCET: finalHashCET });

                }, 1000)
              }

              initialTicker = time
            }

            localStorage.setItem(
              'pollingData',
              JSON.stringify({ id, scgId: staffingCompanyGroupId, autoStartToken, createdAt: moment().valueOf() }),
            );
            this.props.setCollectLoading(true);
            pollCollect(staffingCompanyGroupId, id, fetchAutoStartToken);
          });
        };

        fetch({ url: Endpoints.public.staffingCompanyGroups({ slug }) })
          .then((data) => {
            this.setState({ staffingCompanyGroupId: data.result.id });
            return data.result.id;
          }).then((staffingCompanyGroupId) => {
            const pollingData = localStorage.getItem('pollingData');
            const { id, scgId, autoStartToken, createdAt } = pollingData ?
              JSON.parse(pollingData) : {};
            if (!id || scgId !== staffingCompanyGroupId || moment().diff(createdAt, 'seconds') > 300) {
              fetchAutoStartToken(staffingCompanyGroupId);
              return staffingCompanyGroupId;
            }

            pollCollect(staffingCompanyGroupId, id, fetchAutoStartToken, autoStartToken);
            return staffingCompanyGroupId;
          });
      }
    },
  }),
  withProps({
    mobileDeviceId: getMobileDeviceID(),
  }),
  withProps(({ mobileDeviceId, autoStartToken, secondsDifferenceCET, qrStartToken, qrStartSecret, qrAuthCodeCET }) => {
    switch (mobileDeviceId) {
      case 'android': {
        return {
          mobileUrl: `bankid:///?autostarttoken=${autoStartToken}&redirect=null`,
        };
      }
      case 'ios': {
        const redirect = `${window.location.href.replace(/#\s*$/, '')}#`;
        /*
        // Om man använder den nedan så:
        1. Logga in med bankID
        2. Logga ut
        3. Klicka på logga in igen
        4. Notera att man får en varning BankID-appen kunde inte startas
        5. Klicka på "Öppna", och det verkar variera lite vad som händer (beror på ens token)
        return {
          mobileUrl: `https://app.bankid.com/?autostarttoken=${autoStartToken}&redirect=${redirect}`,
        };
        */
        return {
          mobileUrl: `bankid:///?autostarttoken=${autoStartToken}&redirect=${redirect}`,
        };
      }
      default: {
        return {};
      }
    }
  }),
  withProps(({ mobileDeviceId }) => ({
    isMobile: !!mobileDeviceId,
    mobileDeviceId,
  })),
  withStateHandlers(({ isMobile }) => ({ showQRCode: !isMobile }), {
    toggleQRCode: ({ showQRCode }) => () => ({ showQRCode: !showQRCode }),
  }),
)(Login);